import React from 'react';
import { MapPin, Calendar, HousePlusIcon, ExternalLink } from 'lucide-react';
import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import { formatBudgetLabel } from '@modules/build-hub/utils/format-budget';
import {
    formatProjectDateLabel,
    formatScopeLabel,
} from '@modules/build-hub/utils/format-build-hub-attributes';
import type { BuildHubFieldSelection } from '../../hooks/use-build-hub-field-selection';
import { BuildHubFieldCustomizer } from './BuildHubFieldCustomizer';
import { cn } from '@rocco/utils/cn';
import { useRootData } from '@modules/root';
import { getGoogleStaticMapForBuildHub } from '@modules/build-hub/utils/get-google-static-map';
import { isStreetLevelAddress } from '@modules/build-hub/utils/is-street-level-address';

interface BuildHubInlinePreviewProps {
    /** The BuildHub project to preview */
    buildHub: BuildHubOption;
    /** Current field selection state */
    fieldSelection: BuildHubFieldSelection;
    /** Callback when field selection changes */
    onFieldSelectionChange: (selection: BuildHubFieldSelection) => void;
    /** Additional className */
    className?: string;
}

/**
 * Remove consecutive duplicate parts from a comma-separated address string.
 * e.g. "Auckland, Auckland, New Zealand" becomes "Auckland, New Zealand".
 */
function deduplicateAddressParts(address: string): string {
    return address
        .split(',')
        .map((p) => p.trim())
        .filter((part, i, arr) => i === 0 || part !== arr[i - 1])
        .join(', ');
}

/**
 * Inline preview component that displays selected BuildHub fields.
 * Shows a compact preview with a pencil icon to customize fields.
 */
export function BuildHubInlinePreview({
    buildHub,
    fieldSelection,
    onFieldSelectionChange,
    className,
}: BuildHubInlinePreviewProps) {
    const { config } = useRootData();

    // Get location display text (location is always shared when available)
    const getLocationDisplay = (): string | null => {
        if (fieldSelection.includeFullAddress) {
            const raw =
                buildHub.LocationData?.fullAddress || buildHub.Location || null;
            return raw ? deduplicateAddressParts(raw) : null;
        } else {
            const parts: string[] = [];
            if (buildHub.LocationData?.suburb) {
                parts.push(buildHub.LocationData.suburb);
            }
            if (buildHub.LocationData?.city) {
                parts.push(buildHub.LocationData.city);
            }
            if (parts.length > 0) {
                return deduplicateAddressParts(parts.join(', '));
            }
            return buildHub.LocationData?.region || buildHub.Location || null;
        }
    };

    const locationDisplay = getLocationDisplay();

    // Get display image and map fallback
    const displayImage = buildHub.Thumbnail;
    const displayLocation =
        buildHub.LocationData?.fullAddress || buildHub.Location || null;

    // Generate map fallback if no image but location exists
    // Only show marker pin for street-level addresses
    const mapFallbackUrl =
        !displayImage && displayLocation
            ? getGoogleStaticMapForBuildHub(
                  displayLocation,
                  config.pixels.websiteGoogleMapKey,
                  {
                      showMarker: isStreetLevelAddress(displayLocation),
                      zoomLevel: 12,
                      width: 120,
                      height: 90,
                  },
              )
            : null;

    const mapFallbackUrlMobile =
        !displayImage && displayLocation
            ? getGoogleStaticMapForBuildHub(
                  displayLocation,
                  config.pixels.websiteGoogleMapKey,
                  {
                      showMarker: isStreetLevelAddress(displayLocation),
                      zoomLevel: 15,
                      width: 160,
                      height: 120,
                      markerSize: 'tiny',
                  },
              )
            : null;
    const budgetForFormatting = buildHub.Budget
        ? { min: buildHub.Budget.Min ?? 0, max: buildHub.Budget.Max ?? 0 }
        : null;

    // Build timeline display
    const timelineParts: string[] = [];
    if (fieldSelection.includeStartDate && buildHub.StartDate) {
        timelineParts.push(
            `Start: ${formatProjectDateLabel(buildHub.StartDate, true)}`,
        );
    }
    if (fieldSelection.includeEndDate && buildHub.EndDate) {
        timelineParts.push(
            `End: ${formatProjectDateLabel(buildHub.EndDate, true)}`,
        );
    }
    const timelineDisplay =
        timelineParts.length > 0 ? timelineParts.join('  \u2022  ') : null;

    return (
        <div className={cn('mt-2 space-y-2', className)}>
            {/* Preview Card */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex flex-col items-start justify-between gap-3 md:flex-row">
                    {/* Thumbnail with map fallback */}

                    <div className="w-full md:w-fit md:shrink-0">
                        <div className="aspect-video w-full overflow-hidden rounded-md bg-gray-100 md:h-[90px] md:w-[120px]">
                            {displayImage ? (
                                <img
                                    src={displayImage}
                                    alt={buildHub.Title}
                                    className="h-full w-full object-cover"
                                />
                            ) : mapFallbackUrl && mapFallbackUrlMobile ? (
                                <>
                                    <img
                                        src={mapFallbackUrl}
                                        alt={`Map of ${displayLocation}`}
                                        className="hidden h-full w-full object-cover md:block"
                                    />
                                    <img
                                        src={mapFallbackUrlMobile}
                                        alt={`Map of ${displayLocation}`}
                                        className="block h-full w-full object-cover md:hidden"
                                    />
                                </>
                            ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                    <HousePlusIcon className="h-6 w-6" />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="relative w-full flex-1 space-y-2 md:w-fit">
                        {/* Customize Button */}
                        <div className="absolute top-0 right-0 block md:hidden">
                            <BuildHubFieldCustomizer
                                buildHub={buildHub}
                                fieldSelection={fieldSelection}
                                onFieldSelectionChange={onFieldSelectionChange}
                            />
                        </div>
                        {/* Title - always shown */}
                        <div className="flex items-center gap-1.5">
                            <h4 className="text-sm font-semibold text-gray-900">
                                {buildHub.Title}
                            </h4>
                            <a
                                href={buildHub.Link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 transition-colors hover:text-gray-600"
                                aria-label="Open project in new tab"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                        </div>

                        {/* Project Details */}
                        {(fieldSelection.includeScope ||
                            fieldSelection.includeStage ||
                            fieldSelection.includeBudget) && (
                            <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
                                {/* Scope */}
                                {fieldSelection.includeScope &&
                                    buildHub.ProjectScope && (
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-500">
                                                Scope:
                                            </span>
                                            <span className="text-gray-700">
                                                {formatScopeLabel(
                                                    buildHub.ProjectScope,
                                                )}
                                            </span>
                                        </div>
                                    )}

                                {/* Stage */}
                                {fieldSelection.includeStage &&
                                    buildHub.BuildStage && (
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-500">
                                                Stage:
                                            </span>
                                            <span className="text-gray-700">
                                                {buildHub.BuildStage}
                                            </span>
                                        </div>
                                    )}

                                {/* Budget */}
                                {fieldSelection.includeBudget &&
                                    budgetForFormatting && (
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-500">
                                                Total Project Budget:
                                            </span>
                                            <span className="text-gray-700">
                                                {formatBudgetLabel(
                                                    budgetForFormatting,
                                                )}
                                            </span>
                                        </div>
                                    )}
                            </div>
                        )}

                        {/* Selected Fields */}
                        <div className="space-y-1.5 text-sm">
                            {/* Location */}
                            {locationDisplay && (
                                <div className="flex items-start gap-2">
                                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                                    <span className="text-gray-700">
                                        {locationDisplay}
                                    </span>
                                </div>
                            )}

                            {/* Timeline */}
                            {timelineDisplay && (
                                <div className="flex items-start gap-2">
                                    <Calendar className="h-4 w-4 shrink-0 text-gray-500" />
                                    <span className="text-gray-700">
                                        {timelineDisplay}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Customize Button */}
                    <div className="hidden md:block">
                        <BuildHubFieldCustomizer
                            buildHub={buildHub}
                            fieldSelection={fieldSelection}
                            onFieldSelectionChange={onFieldSelectionChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
